/* global firebase */
import { useState, useEffect } from 'react';

/**
 * Hook to use media for a company. 
 * @param {String} companyId 
 */
export function useMedia(companyId) {
  const [media, setMedia] = useState(null);
  
  useEffect(() => {
    if(!companyId) {
      return;
    }
    return firebase.firestore()
      .collection('companies')
      .doc(companyId)
      .collection('media')
      .orderBy("dateCreated", "desc")
      .onSnapshot(querySnapshot => {
        const newMedia = [];
        querySnapshot.forEach(function(doc) {
          newMedia.push({
            id: doc.id,
            ...doc.data()
          });
        });
        setMedia(newMedia);
      });
  }, [companyId]);
  
  return media;
}


/**
 * Load company media.
 * @async 
 * @param {String} [companyId] 
 * The company ID to load. If none is given, use the current user's company.
 * @returns {Promise<String>} An array of media urls.
 */
export async function loadMedia(companyId) {
  if(!companyId) {
    const auth = firebase.auth().currentUser;
    if(!auth) {
      return;
    }
    companyId = await firebase.firestore()
      .collection('users').doc(auth.uid)
      .get().then(s => s.data().companyId);
  }
  
  return firebase.firestore()
    .collection('companies')
    .doc(companyId)
    .collection('media')
    .orderBy("dateCreated", "desc")
    .get()
    .then(querySnapshot => {
      const newMedia = [];
      querySnapshot.forEach(doc => newMedia.push(doc.data().url));
      return newMedia;
    });
}

/**
 * Upload media.
 * @param  {String}
 * @param  {File}     file 
 * @param  {Function} onProgress
 * @return {Promise}
 * A promise that resolves with the new image URL, or rejects with an error. 
 * A full list of error codes is available at
 * https://firebase.google.com/docs/storage/web/handle-errors
 */
export async function upload(companyId, file, onProgress=()=>null) {
  const mediaRef = await firebase.firestore()
    .collection('companies')
    .doc(companyId)
    .collection('media')
    .add({
      dateCreated: Date.now(),
      userId: firebase.auth().currentUser.uid,
      type: file.type,
    });
    
  const uploadTask = firebase.storage().ref()
    .child(mediaRef.id)
    .put(file);
  
  return new Promise((resolve, reject) => {
    uploadTask.on(firebase.storage.TaskEvent.STATE_CHANGED,
      function(snapshot) {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        mediaRef.update({progress});
        onProgress(progress);
      }, 
      async function(error) {
        await mediaRef.delete();
        reject(error);
      }, 
      async function() {
        const url = await uploadTask.snapshot.ref.getDownloadURL();
        mediaRef.update({
          url, 
          progress: firebase.firestore.FieldValue.delete(),
        });
        resolve(url);
      }
    );
  });
}


/**
 * Upload files to the logged in user's company.
 * @async
 * @param {Array<File>} files Array of file objects.
 */
export async function uploadCompanyMedia(files) {
  const auth = firebase.auth().currentUser;
  if(!auth) {
    return;
  }
  const companyId = await firebase.firestore()
    .collection('users').doc(auth.uid)
    .get().then(s => s.data().companyId);
  
  const urls = [];
  for(const file of files) {
    const url = await upload(companyId, file);
    urls.push(url);
  }
  
  return urls;
}